import React, { useState, useEffect, useMemo, useRef } from "react";
import { Card, Form, Modal, Button } from "react-bootstrap";
import Select from "react-select";
import { Toaster, toast } from "react-hot-toast";
import BankLedgerPDF from "../components/BankLedgerPDF";
import Pagination from "../components/Pagination";
import { PDFViewer } from "@react-pdf/renderer";
import { useButtonTableStyle } from "../utils/useButtonTableStyle";
import { parseApiPagination } from "../utils/parseApiPagination";
import {
  listBankAccounts,
  postBankAccountLedger,
  isErpApiSuccess,
  getErpRequestErrorMessage,
} from "../erpClient";

function BankLedger() {
  const { buttonClass, cardClass, tableClass, buttonTableStyle } =
    useButtonTableStyle();
  const selectStyles = {
    control: (provided) => ({
      ...provided,
      minHeight: 44,
      borderRadius: buttonTableStyle === "square" ? 0 : 8,
      borderColor: "#e0e6eb",
      boxShadow: "none",
      "&:hover": { borderColor: "#e0e6eb" },
    }),
    menu: (provided) => ({
      ...provided,
      borderRadius: buttonTableStyle === "square" ? 0 : 8,
      zIndex: 9999,
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: "0.25rem 0.65rem",
    }),
    placeholder: (provided) => ({
      ...provided,
      color: "#6c757d",
    }),
    singleValue: (provided) => ({
      ...provided,
      color: "#212529",
    }),
  };
  const [selectedBank, setSelectedBank] = useState(null);
  const [bankOptions, setBankOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [entireReport, setEntireReport] = useState(false);
  const [showPDFModal, setShowPDFModal] = useState(false);
  const [pdfData, setPdfData] = useState(null);
  const [ledgerRows, setLedgerRows] = useState([]);
  const [isMetalType, setIsMetalType] = useState(false); // Track if response has metal_type = 1

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMorePages, setHasMorePages] = useState(false);
  const ignoreNextPageFetchRef = useRef(false);
  const pageCacheRef = useRef(new Map());

  const fetchBanks = async () => {
    setLoading(true);
    try {
      const response = await listBankAccounts();
      if (response?.success && Array.isArray(response.data?.bank_accounts)) {
        // Unified bank ledger API expects `bid` = chart `accounts.id` (GL), not `bank_accounts.id`.
        const options = response.data.bank_accounts.map((bank) => {
          const chartBid =
            bank.account_id ??
            bank.accountId ??
            bank.linked_account_id ??
            bank.accounts_id ??
            bank.id;
          return {
            value: chartBid,
            label: bank.bank_name || `Bank ${bank.id}`,
            bankName: bank.bank_name,
            accountHolder: bank.holder_name,
            accountNumber: bank.account_number,
          };
        });
        setBankOptions(options);
      } else {
        setBankOptions([]);
      }
    } catch (error) {
      setBankOptions([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBanks();
  }, []);

  // Listen for API URL changes and refetch data
  useEffect(() => {
    const handleApiUrlChange = (event) => {
      console.log('API URL changed in BankLedger, refetching banks...', event.detail);
      fetchBanks();
      setLedgerRows([]);
      setPdfData(null);
      setShowPDFModal(false);
    };

    window.addEventListener('apiUrlChanged', handleApiUrlChange);

    return () => {
      window.removeEventListener('apiUrlChanged', handleApiUrlChange);
    };
  }, []);

  const handleSubmit = async (
    e,
    { page = currentPage, limit = itemsPerPage, bypassCache = false } = {},
  ) => {
    if (e?.preventDefault) e.preventDefault();
    const refresh = Boolean(e) || bypassCache;
    const cacheKey = entireReport
      ? `${selectedBank?.value || ""}:${page}:${limit}`
      : "";
    if (refresh) pageCacheRef.current.clear();
    if (cacheKey && !refresh) {
      const cached = pageCacheRef.current.get(cacheKey);
      if (cached) {
        setLedgerRows(cached.ledgerRows);
        setPdfData(cached.pdfData);
        setIsMetalType(cached.isMetalType);
        setCurrentPage(cached.currentPage);
        setTotalCount(cached.totalCount);
        setHasMorePages(cached.hasMorePages);
        setLoading(false);
        return;
      }
    }
    setLoading(true);

    const apiPayload = {
      bid: selectedBank.value,
    };

    if (entireReport) {
      apiPayload.type = "entire";
      apiPayload.page = page;
      apiPayload.limit = limit;
    } else {
      if (fromDate) apiPayload.from_date = fromDate;
      if (toDate) apiPayload.to_date = toDate;
    }

    try {
      const payload = await postBankAccountLedger(apiPayload);

      if (!payload || (!isErpApiSuccess(payload) && payload.message !== "radha")) {
        toast.error(
          payload?.message || "Failed to fetch ledger data. Please try again."
        );
        console.error("Unexpected ledger response:", payload);
        setLedgerRows([]); // Clear the table
        setPdfData(null); // Clear PDF data
        return;
      }

      // Check if entries array exists in response
      const entries = payload.data?.entries || [];

      if (!Array.isArray(entries) || entries.length === 0) {
        toast.error("No ledger entries found for this Bank / date range.");
        setLedgerRows([]); // Clear the table
        setPdfData(null); // Clear PDF data
        setTotalCount(0);
        return;
      }

      // Check if response has metal_type = 1 (has gold/silver fields)
      // First check if metal_type is explicitly set in response data
      // Otherwise, check first entry to determine structure
      const responseMetalType = payload.data?.metal_type;
      const firstEntry = entries[0];
      const hasMetalType =
        responseMetalType === 1 ||
        (firstEntry &&
          ("gold_aavak" in firstEntry ||
            "gold_jaavak" in firstEntry ||
            "total_gold" in firstEntry ||
            "silver_aavak" in firstEntry ||
            "silver_jaavak" in firstEntry ||
            "total_silver" in firstEntry));
      setIsMetalType(hasMetalType);

      // Sort entries by date to ensure proper order (YYYY-MM-DD format)
      const sortedEntries = [...entries].sort((a, b) => {
        const dateA = new Date(a.date);
        const dateB = new Date(b.date);
        // If dates are equal, maintain original order
        if (dateA.getTime() === dateB.getTime()) {
          return 0;
        }
        return dateA - dateB;
      });

      // Calculate opening balance from entries before the selected date range
      let openingAmountBalance = 0;
      let transactionsInPeriod = sortedEntries;

      if (!entireReport && fromDate) {
        const from = new Date(fromDate);
        from.setHours(0, 0, 0, 0);

        // Separate entries into opening balance (before fromDate) and period transactions
        const openingEntries = [];
        const periodEntries = [];

        sortedEntries.forEach((entry) => {
          const entryDate = new Date(entry.date);
          entryDate.setHours(0, 0, 0, 0);

          if (entryDate < from) {
            openingEntries.push(entry);
          } else if (!toDate || entryDate <= new Date(toDate)) {
            periodEntries.push(entry);
          }
        });

        // Calculate opening balance from entries before the period
        if (hasMetalType && openingEntries.length > 0) {
          // Metal type structure: use the last entry's total_gold + total_silver as opening balance
          const lastEntry = openingEntries[openingEntries.length - 1];
          const total_gold = parseFloat(lastEntry.total_gold) || 0;
          const total_silver = parseFloat(lastEntry.total_silver) || 0;
          openingAmountBalance = total_gold + total_silver;
        } else {
          // Regular structure: sum up aavak_amt - jaavak_amt
          openingEntries.forEach((entry) => {
            openingAmountBalance +=
              (parseFloat(entry.aavak_amt) || 0) -
              (parseFloat(entry.jaavak_amt) || 0);
          });
        }

        transactionsInPeriod = periodEntries;
      } else {
        // For entire report, opening balance starts at 0
        openingAmountBalance = 0;
      }

      let amountBalance = openingAmountBalance;

      const ledgerData = transactionsInPeriod.map((entry) => {
        let amount_credit = 0;
        let amount_debit = 0;
        let total_balance = 0;

        if (hasMetalType) {
          // Metal type structure: combine gold and silver aavak/jaavak into credit/debit
          const gold_credit = parseFloat(entry.gold_aavak) || 0;
          const gold_debit = parseFloat(entry.gold_jaavak) || 0;
          const silver_credit = parseFloat(entry.silver_aavak) || 0;
          const silver_debit = parseFloat(entry.silver_jaavak) || 0;

          // Total credit/debit is sum of gold and silver
          amount_credit = gold_credit + silver_credit;
          amount_debit = gold_debit + silver_debit;

          // Calculate running balance: add credit, subtract debit
          amountBalance += amount_credit - amount_debit;
          total_balance = amountBalance;
        } else {
          // Regular structure: use aavak_amt and jaavak_amt
          amount_credit = parseFloat(entry.aavak_amt) || 0;
          amount_debit = parseFloat(entry.jaavak_amt) || 0;
          amountBalance += amount_credit - amount_debit;
          total_balance = amountBalance;
        }

        // Use 3 decimal places for metal_type, 2 for regular
        const decimalPlaces = hasMetalType ? 3 : 2;

        return {
          date: entry.date || "-",
          credit: amount_credit ? amount_credit.toFixed(decimalPlaces) : "",
          debit: amount_debit ? amount_debit.toFixed(decimalPlaces) : "",
          balance: total_balance
            ? total_balance.toFixed(decimalPlaces)
            : `0.${"0".repeat(decimalPlaces)}`,
          remarks: entry.remarks || "",
          type: entry.type || "",
        };
      });

      const finalLedgerData = [...ledgerData];

      // Save for table view
      setLedgerRows(finalLedgerData);

      const paginationMeta = entireReport
        ? parseApiPagination(payload, finalLedgerData.length)
        : null;

      if (entireReport && paginationMeta) {
        setCurrentPage(paginationMeta.page);
        setTotalCount(paginationMeta.total);
        setHasMorePages(paginationMeta.hasMore);
        if (paginationMeta.limit) setItemsPerPage(paginationMeta.limit);
      } else {
        setCurrentPage(1);
        setTotalCount(finalLedgerData.length);
        setHasMorePages(false);
      }

      // Also prepare data for PDF (used when user clicks Print)
      const nextPdf = {
        ledgerData: finalLedgerData,
        bankInfo: selectedBank,
        fromDate,
        toDate,
        entireReport,
      };
      setPdfData(nextPdf);
      if (cacheKey && paginationMeta) {
        pageCacheRef.current.set(cacheKey, {
          ledgerRows: finalLedgerData,
          pdfData: nextPdf,
          isMetalType: hasMetalType,
          currentPage: paginationMeta.page,
          totalCount: paginationMeta.total,
          hasMorePages: paginationMeta.hasMore,
        });
      }
      toast.success("Ledger generated successfully!");
    } catch (error) {
      toast.error(
        getErpRequestErrorMessage(
          error,
          "An error occurred while fetching the ledger data."
        )
      );
      console.error("Error fetching bank ledger:", error);
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    if (!pdfData || !ledgerRows.length) {
      toast.error("Please generate the ledger first.");
      return;
    }
    setShowPDFModal(true);
  };

  // Memoize the PDF component to avoid unnecessary rerenders
  const pdfViewer = useMemo(() => {
    if (!pdfData) return null;
    return (
      <PDFViewer width="100%" height="100%">
        <BankLedgerPDF
          ledgerData={pdfData.ledgerData}
          bankInfo={pdfData.bankInfo}
          fromDate={pdfData.fromDate}
          toDate={pdfData.toDate}
          entireReport={pdfData.entireReport}
        />
      </PDFViewer>
    );
  }, [pdfData]);

  return (
    <div className="body-wrapper">
      <Toaster />
      <div className="container-fluid">
        <div className={`card ${cardClass}`}>
          <div className="border-bottom title-part-padding">
            <h4 className="card-title mb-0">Bank Ledger</h4>
          </div>
          <div className="card-body">
            <form noValidate="" onSubmit={handleSubmit}>
              <div className="row align-items-end">
                {/* Bank Dropdown */}
                <div className="mb-3 col-md-3">
                  <label className="form-label">Select Bank Account</label>
                  <Select
                    options={bankOptions}
                    value={selectedBank}
                    onChange={setSelectedBank}
                    placeholder={
                      loading ? "Loading..." : "Search and select Bank..."
                    }
                    isClearable
                    isSearchable
                    isLoading={loading}
                    styles={selectStyles}
                  />
                </div>
                {/* From Date */}
                <div className="mb-3 col-md-2">
                  <label className="form-label">From Date</label>
                  <input
                    type="date"
                    className={`form-control ${buttonClass}`}
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    disabled={entireReport}
                  />
                </div>
                {/* To Date */}
                <div className="mb-3 col-md-2">
                  <label className="form-label">To Date</label>
                  <input
                    type="date"
                    className={`form-control ${buttonClass}`}
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    disabled={entireReport}
                  />
                </div>
                {/* Entire Report Checkbox */}
                <div
                  className="mb-3 col-md-2 d-flex align-items-center"
                  style={{ marginTop: 32 }}
                >
                  <Form.Check
                    type="checkbox"
                    id="entireReportCheckbox"
                    label="Entire Report"
                    checked={entireReport}
                    onChange={(e) => {
                      setEntireReport(e.target.checked);
                      setCurrentPage(1);
                    }}
                  />
                </div>
                {/* Submit Button */}
                <div
                  className="mb-3 col-md-2 d-flex align-items-center"
                  style={{ marginTop: 32 }}
                >
                  <button
                    type="submit"
                    className={`btn btn-primary w-100 ${buttonClass}`}
                    disabled={!selectedBank || loading}
                  >
                    {loading ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        />
                        Loading...
                      </>
                    ) : (
                      "Submit"
                    )}
                  </button>
                </div>
              </div>
            </form>
            {/* Ledger Table */}
            {(ledgerRows.length > 0 || (entireReport && totalCount > 0)) && (
              <div className="mt-4">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="mb-0">
                    Ledger - {selectedBank?.label ? selectedBank.label : ""}
                    {entireReport && totalCount > 0 ? (
                      <span className="text-muted fs-6 ms-2">
                        (Page {currentPage} of{" "}
                        {Math.max(1, Math.ceil(totalCount / itemsPerPage))})
                      </span>
                    ) : null}
                  </h5>
                  <button
                    type="button"
                    className={`btn btn-secondary ${buttonClass}`}
                    onClick={handlePrint}
                  >
                    Print
                  </button>
                </div>
                <div className={`table-responsive mb-4 border ${cardClass}`}>
                  <table
                    className={`table text-nowrap mb-0 align-middle ${tableClass}`}
                  >
                    <thead className="text-dark fs-4">
                      <tr>
                        <th className="text-nowrap">Date</th>
                        <th className="text-nowrap">Type</th>
                        <th className="text-end text-nowrap text-success">
                          Credit
                        </th>
                        <th
                          className="text-end text-nowrap"
                          style={{ color: "#dc3545" }}
                        >
                          Debit
                        </th>
                        <th className="text-end text-nowrap">Balance</th>
                        <th className="text-nowrap">Remarks</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ledgerRows.map((row, idx) => {
                        const cellStyle = row.isOpening
                          ? { backgroundColor: "#E3F2FD", fontWeight: 500 }
                          : undefined;
                        return (
                          <tr key={idx}>
                            <td style={cellStyle}>
                              {row.isOpening ? "-" : row.date}
                            </td>
                            <td style={cellStyle}>{row.type || "-"}</td>
                            <td
                              className={`text-end ${row.credit ? "text-success" : ""
                                }`}
                              style={cellStyle}
                            >
                              {row.credit || "-"}
                            </td>
                            <td
                              className="text-end"
                              style={{
                                ...cellStyle,
                                color: row.debit ? "#dc3545" : undefined,
                              }}
                            >
                              {row.debit || "-"}
                            </td>
                            <td
                              className="text-end"
                              style={{
                                ...cellStyle,
                                color: row.isOpening
                                  ? undefined
                                  : parseFloat(row.balance || 0) < 0
                                    ? "#dc3545"
                                    : parseFloat(row.balance || 0) > 0
                                      ? "#28a745"
                                      : undefined,
                              }}
                            >
                              {row.balance || "-"}
                            </td>
                            <td style={cellStyle}>{row.remarks || "-"}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {entireReport && totalCount > 0 && (
                  <Pagination
                    totalItems={totalCount}
                    hasMore={hasMorePages}
                    itemsPerPage={itemsPerPage}
                    currentPage={currentPage}
                    onPageChange={(page) => {
                      if (ignoreNextPageFetchRef.current) {
                        ignoreNextPageFetchRef.current = false;
                        return;
                      }
                      handleSubmit(undefined, { page, limit: itemsPerPage });
                    }}
                    onItemsPerPageChange={(limit) => {
                      ignoreNextPageFetchRef.current = true;
                      setItemsPerPage(limit);
                      setCurrentPage(1);
                      handleSubmit(undefined, {
                        page: 1,
                        limit,
                        bypassCache: true,
                      });
                    }}
                    itemsPerPageOptions={[10, 20, 30, 50, 100]}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      {/* PDF Modal */}
      <Modal
        show={showPDFModal}
        onHide={() => setShowPDFModal(false)}
        size="xl"
        centered
        dialogClassName="modal-90w"
        contentClassName={buttonTableStyle === "square" ? "rounded-0" : ""}
      >
        <Modal.Header closeButton>
          <Modal.Title>Bank Ledger PDF Preview</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ height: "80vh" }}>
          {showPDFModal && pdfViewer}
        </Modal.Body>
      </Modal>
    </div>
  );
}

export default BankLedger;
